import qz from 'qz-tray'
import rs from 'jsrsasign'
import { useToast } from '../useToast'
import isEmpty from 'lodash/isEmpty'
import { useCallback } from 'react'
import { useQzOptions } from './useQzOptions'
import find from 'lodash/find'

export type QzOption = {
  certificate?: string
  signature?: string
  host?: string
}

export type QzConsumerProps = {
  useQz: (props?: UseQzProps) => UseQz
}

export type UseQz = {
  initialize: (options?: QzOption) => Promise<boolean>
  qzOptions: QzOption[]
}

export type UseQzProps = QzOption

export const useQz = (props?: UseQzProps): UseQz => {
  const { showToast } = useToast()
  const qzOptions = useQzOptions()

  const initialize = useCallback(
    async (options?: QzOption) => {
      //merge the options
      const mergeOptions = {
        ...props,
        ...options,
      }

      // the required field from user template setting is just qzOption.host
      // the qzOption.certificate and qzOption.signature are optional, and it will be retrieved from the qzOptions (filtering by host)
      // if it cannot be found, it will  return what ever is in the qzOption is
      if (!isEmpty(mergeOptions.host)) {
        const foundQzOption = find(qzOptions, { host: mergeOptions.host })
        if (!isEmpty(foundQzOption)) {
          mergeOptions.certificate = foundQzOption.certificate
          mergeOptions.signature = foundQzOption.signature
        }
      }
      if (isEmpty(mergeOptions.certificate) || isEmpty(mergeOptions.signature)) return false

      await qz.security.setCertificatePromise((resolve) => {
        resolve(mergeOptions.certificate)
      })

      await qz.security.setSignatureAlgorithm('SHA512') // Since 2.1

      await qz.security.setSignaturePromise(function (toSign) {
        return function (resolve) {
          try {
            const pk = rs.KEYUTIL.getKey(mergeOptions.signature)
            const sig = new rs.KJUR.crypto.Signature({ alg: 'SHA512withRSA' }) // Use "SHA1withRSA" for QZ Tray 2.0 and older
            sig.init(pk)
            sig.updateString(toSign)
            const hex = sig.sign()
            resolve(rs.stob64(rs.hextorstr(hex)))
          } catch (err) {
            showToast({
              type: 'danger',
              message: err?.message,
            })
          }
        }
      })

      //this will do the initial connection
      //if there is no host, it will connect to localhost
      if (mergeOptions.host) {
        try {
          await qz.websocket.connect({ host: mergeOptions.host })
        } catch {
          //fallback to the local host if it cannot connect
          await qz.websocket.connect()
        }
      } else {
        await qz.websocket.connect()
      }

      return true
    },
    // props and showToast intentionally excluded from the whole-object/whole-function form: the
    // individual props fields already used here (certificate/signature/host) are the actual inputs
    // read in this callback, kept granular on purpose so a caller passing a new `props` object literal
    // every render doesn't recreate `initialize`. showToast (from useToast) is likewise a new function
    // every render of ToastProvider, and is only reached inside the signature-error callback, not on
    // every call, so tracking it here would recreate `initialize` — and reconnect qz-tray — far more
    // often than the certificate/signature/host it actually depends on change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props?.certificate, props?.signature, props?.host, qzOptions]
  )

  return {
    initialize,
    qzOptions,
  }
}
